import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from '../ui';
import PremiumName from '../ui/PremiumName';
import { useAuth } from '../../hooks/useAuth';
import { useUserData } from '../../hooks/useUserData';
import { useToast } from '../../contexts/ToastContext';
import { toggleSavePost } from '../../services/savedService';
import { formatPostDate, formatFullDate } from '../../utils/dateFormatter';
import { getCategoryName } from '../../constants/categories';
import { DEFAULT_IMAGES } from '../../constants/defaults';

/**
 * PostCard - Thumbnail para grid masonry
 * Optimizado para exploración visual rápida con carrusel
 * Props opcionales: currentUserId, savedPostIds, onToggleSave (si no se pasan, se usan de useAuth)
 */
const PostCard = ({ post, onClick, currentUserId: currentUserIdProp, savedPostIds: savedPostIdsProp, onToggleSave }) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { showToast } = useToast();
  const currentUserId = currentUserIdProp ?? userData?.id;
  const savedPostIds = savedPostIdsProp ?? (userData?.savedPosts || []);
  const isSaved = currentUserId && Array.isArray(savedPostIds) && savedPostIds.includes(post.id);

  const handleToggleSave = async (e) => {
    e?.stopPropagation();
    if (!currentUserId) return;
    const result = await toggleSavePost(currentUserId, post.id);
    if (result.success && result.saved) {
      showToast('Post guardado');
    }
    if (onToggleSave && result.success && !result.saved) {
      onToggleSave(post.id);
    }
  };

  // Para posts del usuario actual: AuthContext (tiempo real). Otros: useUserData (cache 5min)
  const isOwnPost = post?.userId === currentUserId;
  const { userData: authorData } = useUserData(isOwnPost ? null : post?.userId);
  const liveAuthorData = isOwnPost ? userData : authorData;
  const authorName = post?.authorInfo?.displayName || post?.userName || liveAuthorData?.displayName || 'Usuario';
  const authorPhotoURL = isOwnPost
    ? (liveAuthorData?.photoURL ?? null)
    : (post?.authorInfo?.profileImage || post?.userPhotoURL || liveAuthorData?.photoURL || null);
  const isAuthorPremium = liveAuthorData?.isPremium ?? post?.authorInfo?.isPremium ?? post?.isPremiumAuthor ?? false;
  const authorPremiumColor = liveAuthorData?.premiumNameColor ?? post?.authorInfo?.premiumNameColor ?? post?.premiumNameColor ?? null;
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [imageError, setImageError] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const hasMultipleMedia = post.media && post.media.length > 1;
  const currentMedia = post.media && post.media.length > 0 ? post.media[currentMediaIndex] : null;

  const handleMediaLoad = (index) => {
    setLoadedImages(prev => new Set([...prev, index]));
  };

  const isCurrentImageLoaded = loadedImages.has(currentMediaIndex);

  // Navegación del carrusel con transición suave
  const handlePrevMedia = (e) => {
    e.stopPropagation();
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentMediaIndex((prev) => (prev === 0 ? post.media.length - 1 : prev - 1));
      setIsTransitioning(false);
    }, 150);
  };

  const handleNextMedia = (e) => {
    e.stopPropagation();
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentMediaIndex((prev) => (prev === post.media.length - 1 ? 0 : prev + 1));
      setIsTransitioning(false);
    }, 150);
  };

  const handleDotClick = (e, index) => {
    e.stopPropagation();
    if (isTransitioning || index === currentMediaIndex) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentMediaIndex(index);
      setIsTransitioning(false);
    }, 150);
  };

  const handleCardClick = () => navigate(`/post/${post.id}`);

  return (
    <div
      className="group cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="bg-white dark:bg-slate-800 rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 dark:border-slate-700">
        
        {/* Imagen/Thumbnail con Carrusel */}
        {currentMedia ? (
          <div className="relative overflow-hidden bg-gray-100 dark:bg-slate-700">
            {/* Loading skeleton - solo para la primera carga */}
            {!isCurrentImageLoaded && !imageError && (
              <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-slate-600" />
            )}

            {post.media.map((media, index) => {
              const isVideo = media.type === 'video';
              const isActive = index === currentMediaIndex;
              const visibilityClass = isActive
                ? `opacity-100 ${isTransitioning ? 'scale-95' : 'group-hover:scale-105'}`
                : 'absolute inset-0 opacity-0 pointer-events-none';
              const style = { transition: 'opacity 300ms, transform 300ms', ...(index !== currentMediaIndex && { position: 'absolute', top: 0, left: 0 }) };
              if (isVideo) {
                return (
                  <div key={index} className={`w-full h-auto object-cover transition-all duration-300 relative ${visibilityClass}`} style={style}>
                    <video
                      src={media.url}
                      poster={media.thumbnailUrl || DEFAULT_IMAGES.VIDEO_POSTER}
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                      onLoadedData={() => handleMediaLoad(index)}
                      onError={() => setImageError(true)}
                      preload={index === 0 ? 'metadata' : 'none'}
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                        <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <img
                  key={index}
                  src={media.url}
                  alt={post.caption || `Imagen ${index + 1}`}
                  className={`w-full h-auto object-cover transition-all duration-300 ${visibilityClass}`}
                  style={style}
                  onLoad={() => handleMediaLoad(index)}
                  onError={() => setImageError(true)}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              );
            })}

            {/* Botón guardar - esquina superior derecha */}
            {currentUserId && (
              <button
                type="button"
                onClick={handleToggleSave}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white transition-all opacity-0 group-hover:opacity-100 z-10"
                title={isSaved ? 'Quitar de guardados' : 'Guardar'}
                aria-label={isSaved ? 'Quitar de guardados' : 'Guardar'}
              >
                {isSaved ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M5 2h14a1 1 0 011 1v19.143a1 1 0 01-1.581.814L12 18.499l-6.419 4.458A1 1 0 014 22.143V3a1 1 0 011-1z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-5-7 5V5z" />
                  </svg>
                )}
              </button>
            )}

            {/* Badge de categoría - esquina superior izquierda */}
            {post.category && (
              <span className="absolute top-2 left-2 text-xs bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm px-2 py-1 rounded-full text-gray-700 dark:text-gray-300 font-medium border border-gray-200 dark:border-slate-600">
                {getCategoryName(post.category)}
              </span>
            )}

            {/* Botones de navegación - Solo si hay múltiples imágenes */}
            {hasMultipleMedia && (
              <>
                {/* Botón anterior */}
                <button
                  onClick={handlePrevMedia}
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 w-6 h-6 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Imagen anterior"
                >
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Botón siguiente */}
                <button
                  onClick={handleNextMedia}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
                  aria-label="Imagen siguiente"
                >
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Indicadores de paginación (dots) */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 px-1.5 py-1 bg-black/50 backdrop-blur-sm rounded-full">
                  {post.media.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => handleDotClick(e, index)}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        index === currentMediaIndex
                          ? 'bg-white w-3'
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                      aria-label={`Ir a imagen ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}

              {/* Overlay con info al hover - Stats en la esquina inferior izquierda */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              {/* Gradiente solo en la parte inferior */}
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/80 to-transparent" />
              
              {/* Stats fijos en la esquina inferior izquierda */}
              <div className="absolute bottom-2 left-2 flex items-center gap-3 text-white text-xs z-10">
                <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">{post.likesCount || 0}</span>
                </div>

                <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="font-medium">{post.commentsCount || 0}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Post solo texto */
          <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-slate-800 dark:to-slate-700 min-h-[150px] flex items-center justify-center">
            <p className="text-sm text-gray-700 dark:text-gray-300 text-center line-clamp-4">
              {post.caption || post.text}
            </p>
          </div>
        )}

        {/* Footer con info del autor */}
        <div className="p-2.5 mt-auto">
          <div className="flex items-center gap-2">
            <Link 
              to={`/profile/${post.userId}`}
              className="flex items-center gap-1.5 flex-1 min-w-0 hover:opacity-80 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar
                src={authorPhotoURL}
                alt={authorName}
                fallbackText={authorName}
                size="sm"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <PremiumName
                    name={authorName}
                    isPremium={isAuthorPremium}
                    premiumColor={authorPremiumColor}
                    nameClassName="text-xs font-medium text-gray-900 dark:text-white"
                    iconSize="xs"
                  />
                  {post.createdAt && (
                    <span 
                      className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap"
                      title={formatFullDate(post.createdAt)}
                    >
                      • {formatPostDate(post.createdAt)}
                    </span>
                  )}
                </div>
                {post.caption && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                    {post.caption}
                  </p>
                )}
              </div>
            </Link>

            {/* Badge de servicio si aplica */}
            {post.isService && (
              <div className="flex-shrink-0">
                <span className="inline-flex items-center px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-[10px] font-medium rounded-full">
                  💼
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCard;
